import type { RowData } from "@tanstack/react-table";
import type { DataGridRow, DataGridTable } from "../../columns/types";
import type { GridController } from "../../focus/gridController";
import { isValueColumn } from "../../toolbar/columnSettingsActions";
import { findHeader } from "./columnDrag";
import {
    type DataGridRowDropPosition,
    type DataGridRowMove,
    type DropRow,
    getPointerDropTarget,
    type RowDropTarget,
} from "./rowDropTarget";

/** The rows a drag works over, snapshotted when it starts. */
export interface RowDragSnapshot {
    rows: DropRow[];
    sourceIndex: number;
    isTree: boolean;
    canDrop: ((move: DataGridRowMove) => boolean) | undefined;
}

/** Where the indicator draws, in viewport content coordinates. */
export interface RowIndicatorState {
    target: RowDropTarget;
    top: number;
    /** 0 for a line; the row's height for `into`. */
    height: number;
    /** Where a depth-0 label starts, so the line indents like the rows. */
    indent: number;
}

export const toDropRows = <TData extends RowData>(
    rows: ReadonlyArray<DataGridRow<TData>>,
): DropRow[] =>
    rows.map((row) => ({
        id: row.id,
        depth: row.depth,
        parentId: row.parentId ?? null,
        hasShownChildren: row.getIsExpanded() && row.subRows.length > 0,
        hasChildren: row.subRows.length > 0,
    }));

/** The target under a pointer at `clientY`, from the body's row geometry. */
export const getRowTargetAt = ({
    controller,
    snapshot,
    clientY,
}: {
    controller: GridController;
    snapshot: RowDragSnapshot;
    clientY: number;
}): RowDropTarget | null => {
    const { body } = controller;
    if (!body) {
        return null;
    }
    const offset = clientY - body.getBoundingClientRect().top;
    const index = controller.getRowIndexAtOffset(offset);
    const bounds = controller.getRowBounds(index);
    if (!bounds || bounds.size <= 0) {
        return null;
    }
    const fraction = Math.min(
        Math.max((offset - bounds.start) / bounds.size, 0),
        1,
    );
    return getPointerDropTarget(snapshot, { index, fraction });
};

/** Content x where the first value column's text starts. */
export const getLabelIndent = <TData extends RowData>({
    table,
    viewport,
}: {
    table: DataGridTable<TData>;
    viewport: HTMLElement;
}) => {
    const column = table.getVisibleLeafColumns().find(isValueColumn);
    const header = column && findHeader(viewport, column.id);
    if (!header) {
        return 0;
    }
    const left =
        header.getBoundingClientRect().left -
        viewport.getBoundingClientRect().left +
        viewport.scrollLeft;
    return (
        left +
        (Number.parseFloat(getComputedStyle(header).paddingInlineStart) || 0)
    );
};

export const getRowIndicator = ({
    controller,
    target,
    indent,
}: {
    controller: GridController;
    target: RowDropTarget;
    indent: number;
}): RowIndicatorState | null => {
    const bounds = controller.getRowBounds(target.indicatorIndex);
    if (!bounds) {
        return null;
    }
    const bodyTop = controller.body?.offsetTop ?? 0;
    const top =
        target.position === "after" ? bounds.start + bounds.size : bounds.start;
    return {
        target,
        top: bodyTop + top,
        height: target.position === "into" ? bounds.size : 0,
        indent,
    };
};

export const isSameTarget = (
    a: RowDropTarget | null,
    b: RowDropTarget | null,
) => a?.targetRowId === b?.targetRowId && a?.position === b?.position;

/** The row's name in the preview and announcements. */
export const getRowLabel = <TData extends RowData>({
    row,
    getLabel,
}: {
    row: DataGridRow<TData> | undefined;
    getLabel: ((original: TData) => string) | undefined;
}) => {
    if (!row) {
        return "Row";
    }
    return getLabel?.(row.original) ?? `Row ${row.index + 1}`;
};

const POSITION_WORDS: Record<DataGridRowDropPosition, string> = {
    before: "before",
    after: "after",
    into: "inside",
};

/** "before Rent", for announcements. */
export const describePosition = ({
    position,
    targetLabel,
}: {
    position: DataGridRowDropPosition;
    targetLabel: string;
}) => `${POSITION_WORDS[position]} ${targetLabel}`;

/** The keyboard step's announcement, e.g. "Before Acme Supplies." */
export const describeTargetStep = (target: {
    position: DataGridRowDropPosition;
    targetLabel: string;
}) => {
    const words = POSITION_WORDS[target.position];
    return `${words.charAt(0).toUpperCase()}${words.slice(1)} ${target.targetLabel}.`;
};
